import { Autocomplete, Box, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box as BoxIcon, FileText, Search, User } from 'react-feather';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../authentication/useAuth';
import { tokens } from '../../../theme';
import instance from '../../../utils/imsAxios';
import { jobCardTotal } from '../../../utils/jobCardTotals';

// Loaded on first use and kept for a minute, so typing never waits on the network
const FRESH_MS = 60 * 1000;
const PER_GROUP = 6;

const money = (v) => `$${(Math.round(v * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const paidOf = (jc) => (jc.payments ?? []).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
// Old records hold the text 'null' where a value was never entered
const clean = (v) => (v == null || ['null', 'undefined'].includes(String(v).trim().toLowerCase()) ? '' : String(v).trim());
const customerName = (c) => `${clean(c?.firstName)} ${clean(c?.lastName)}`.replace(/\s+/g, ' ').trim();
// DD-MM-YYYY as a number that sorts newest first
const dateOrder = (d) => { const [day, month, year] = String(d ?? '').split('-'); return Number(`${year}${month}${day}`) || 0; };

// A match on the start of a field ranks above a match inside it; no match is null
const rank = (query, fields) => {
    let best = null;
    for (const field of fields) {
        const text = String(field ?? '').toLowerCase();
        if (!text) continue;
        if (text.startsWith(query)) return 0;
        if (best === null && text.includes(query)) best = 1;
    }
    return best;
};

const ICONS = { 'Job cards': FileText, Customers: User, 'Inventory items': BoxIcon };

const GlobalSearch = ({ autoFocus = false, onDone }) => {
    const navigate = useNavigate();
    const { companyId, canView, canDelete } = useAuth();
    const { siteSelected } = useSelector((state) => state.global);
    const site = siteSelected ?? JSON.parse(localStorage.getItem('selectedSite') ?? 'null');
    const showPricing = canView('job-card-pricing');
    const [data, setData] = useState({ jobCards: [], customers: [], items: [] });
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');
    const loadedAt = useRef(0);
    const inputRef = useRef(null);

    const load = useCallback(async () => {
        if (!companyId || !site?.siteId || loading || Date.now() - loadedAt.current < FRESH_MS) return;
        setLoading(true);
        const get = (allowed, url, params) => (allowed ? instance.get(url, { params }).then((r) => r.data ?? []).catch(() => []) : Promise.resolve([]));
        const [jobCards, customers, items] = await Promise.all([
            // Only users who can delete job cards see the archived (paid or closed) list; everyone else searches active ones
            get(canView('job-cards'), canDelete('job-cards') ? '/jobCards/all' : '/jobCards', { companyId, siteId: site.siteId }),
            get(canView('customers') || canView('job-cards'), '/customers', { companyId }),
            get(canView('inventory-items'), '/items', { companyId, siteId: site.siteId })
        ]);
        setData({ jobCards, customers, items });
        loadedAt.current = Date.now();
        setLoading(false);
    }, [companyId, site?.siteId, canView, canDelete, loading]);

    // Ctrl+K or / anywhere jumps to the search box, unless the user is typing somewhere else
    useEffect(() => {
        const onKey = (e) => {
            const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable;
            if ((e.key === 'k' && (e.ctrlKey || e.metaKey)) || (e.key === '/' && !typing)) {
                e.preventDefault();
                inputRef.current?.focus();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const options = useMemo(() => {
        const q = query.toLowerCase().trim();
        if (q.length < 2) return [];
        const customersById = new Map(data.customers.map((c) => [c._id, c]));
        const top = (list) => list.filter((o) => o.rank !== null).sort((a, b) => a.rank - b.rank || (b.order ?? 0) - (a.order ?? 0) || a.sort.localeCompare(b.sort)).slice(0, PER_GROUP);

        const jobCards = top(data.jobCards.map((jc) => {
            const customer = customersById.get(jc.customerId);
            const number = `${site?.siteCode ? `${site.siteCode}-` : ''}${jc.jobCardNumber}`;
            const total = jobCardTotal(jc.items);
            const owing = total - paidOf(jc);
            const status = jc.reasonClosed ? 'Closed' : owing < 1 ? 'Paid' : 'Unpaid';
            return {
                group: 'Job cards', key: `jc-${jc._id}`, record: jc, customer,
                rank: rank(q, [jc.jobCardNumber, number, customerName(customer), customer?.customerNumber, jc.comment]),
                sort: jc.jobCardNumber ?? '',
                order: dateOrder(jc.dateIssued),
                primary: number,
                secondary: [customerName(customer) || customer?.customerNumber, jc.dateIssued, showPricing ? `${status}${status === 'Unpaid' ? ` ${money(owing)}` : ''}` : status].filter(Boolean).join(', ')
            };
        }));
        const customers = top(data.customers.map((c) => ({
            group: 'Customers', key: `cu-${c._id}`, record: c,
            rank: rank(q, [c.customerNumber, customerName(c), clean(c.lastName), clean(c.contactNumber), clean(c.idNumber)]),
            sort: customerName(c),
            primary: customerName(c) || c.customerNumber,
            secondary: [c.customerNumber, clean(c.contactNumber)].filter(Boolean).join(', ')
        })));
        const items = top(data.items.map((it) => ({
            group: 'Inventory items', key: `it-${it._id}`, record: it,
            rank: rank(q, [it.itemName]),
            sort: it.itemName ?? '',
            primary: it.itemName,
            secondary: [it.unit, it.serviceItem ? 'Service' : it.vehicleItem ? 'Vehicle' : it.compositeItem ? 'Composite' : `In stock: ${it.currentStockLevel ?? 0}`].filter(Boolean).join(', ')
        })));
        return [...jobCards, ...(canView('customers') ? customers : []), ...(canView('inventory-items') ? items : [])];
    }, [query, data, site?.siteCode, showPricing, canView]);

    const open = (option) => {
        if (!option) return;
        if (option.group === 'Job cards') navigate('/ims/operations/job-cards', { state: { openJobCardId: option.record._id, customer: option.customer } });
        if (option.group === 'Customers') navigate('/ims/administration/customers', { state: { openCustomer: option.record } });
        if (option.group === 'Inventory items') navigate('/ims/administration/inventory-items', { state: { search: option.record.itemName } });
        // The next search reloads, so it never offers a record changed since
        loadedAt.current = 0;
        setQuery('');
        inputRef.current?.blur();
        onDone?.();
    };

    const q = query.trim();
    return (
        <Autocomplete
            options={options}
            groupBy={(o) => o.group}
            getOptionLabel={(o) => o.primary ?? ''}
            isOptionEqualToValue={(a, b) => a.key === b.key}
            filterOptions={(x) => x}
            value={null}
            inputValue={query}
            onInputChange={(e, value, reason) => reason !== 'reset' && setQuery(value)}
            onChange={(e, option) => open(option)}
            onOpen={load}
            loading={loading}
            blurOnSelect
            // No drop-down arrow: the end of the box holds the Ctrl K hint while empty, and the clear button once typed in
            forcePopupIcon={false}
            clearOnBlur={false}
            openOnFocus={false}
            noOptionsText={q.length < 2 ? 'Type at least 2 letters' : loading ? 'Loading…' : `Nothing found for "${q}"`}
            fullWidth
            renderOption={(props, o) => {
                const Icon = ICONS[o.group];
                return (
                    <Box component="li" {...props} key={o.key} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start !important' }}>
                        <Icon size={16} style={{ marginTop: 3, flexShrink: 0, color: tokens.navInkMuted }} />
                        <Box minWidth={0}>
                            <Typography fontSize={14} fontWeight={500} noWrap>{o.primary}</Typography>
                            <Typography fontSize={12} color="text.secondary" noWrap>{o.secondary}</Typography>
                        </Box>
                    </Box>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    inputRef={inputRef}
                    autoFocus={autoFocus}
                    size="small"
                    placeholder="Search job cards, customers, items"
                    onFocus={load}
                    inputProps={{ ...params.inputProps, 'aria-label': 'Search job cards, customers and inventory items' }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: <InputAdornment position="start"><Search size={16} color={tokens.navInkMuted} /></InputAdornment>,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={14} /> : !query && <Typography component="span" fontSize={11} color={tokens.navInkMuted} sx={{ display: { xs: 'none', md: 'inline' }, border: `1px solid ${tokens.navDivider}`, borderRadius: '4px', px: 0.5 }}>Ctrl K</Typography>}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '999px', backgroundColor: tokens.navActive, fontSize: 14, pr: '12px !important' } }}
                />
            )}
        />
    );
};

export default GlobalSearch;
